import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { LayoutService } from './service/app.layout.service';
import { Router } from '@angular/router';
import { UsuarioService } from 'src/services/usuario.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-menu-profile',
    templateUrl: './app.menuprofile.component.html',
    styleUrls: ['./app.menuprofile.component.scss'],
    animations: [
        trigger('menu', [
            transition('void => inline', [
                style({ height: 0 }),
                animate('400ms cubic-bezier(0.86, 0, 0.07, 1)', style({ opacity: 1, height: '*' })),
            ]),
            transition('inline => void', [
                animate('400ms cubic-bezier(0.86, 0, 0.07, 1)', style({ opacity: 0, height: '0' }))
            ]),
            transition('void => overlay', [
                style({ opacity: 0, transform: 'scaleY(0.8)' }),
                animate('.12s cubic-bezier(0, 0, 0.2, 1)')
            ]),
            transition('overlay => void', [
                animate('.1s linear', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class AppMenuProfileComponent implements OnInit {
    readonly defaultUserImage = 'assets/layout/images/avatar.png';

    constructor(
        public layoutService: LayoutService,
        public el: ElementRef,
        private router: Router,
        protected usuarioService: UsuarioService,
        protected formBuilder: FormBuilder,
        private messageService: MessageService,
    ) { }

    user = JSON.parse(localStorage.getItem('user') ?? '{}');
    form: FormGroup;
    displayModal = false;
    loading = false;
    imageLoadFailed = false;
    imagePreviewUrl: string | null = null;
    selectedImageFile: File | null = null;
    imagemRemovida = false;
    private isRefreshingUserImage = false;
    private lastRetriedUserImageUrl: string | null = null;
    private lastRetriedPreviewImageUrl: string | null = null;

    @ViewChild('profileImageUpload') profileImageUpload?: ElementRef<HTMLInputElement>;

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            id: [null],
            name: [null, Validators.required],
            url_imagem_usuario: [null],
            current_password: [null],
            password: [null],
            password_confirmation: [null],
        });

        this.refreshCurrentUser();
    }

    getInitial(name: string): string {
        if (!name) return '';
        return name.trim().charAt(0);
    }

    get shouldShowUserImage(): boolean {
        return !!(this.user?.url_imagem_usuario?.trim()) && !this.imageLoadFailed;
    }

    get shouldShowPasswordConfirmation(): boolean {
        return !!this.form?.get('password')?.value;
    }

    get profileAvatarSrc(): string {
        if (this.imagePreviewUrl) {
            return this.imagePreviewUrl;
        }

        const url = this.form?.get('url_imagem_usuario')?.value;
        if (url && !this.imagemRemovida) {
            return url;
        }

        return this.defaultUserImage;
    }

    get hasProfileImage(): boolean {
        return !!(this.imagePreviewUrl || (this.form?.get('url_imagem_usuario')?.value && !this.imagemRemovida));
    }

    clearImage(): void {
        this.selectedImageFile = null;
        this.imagePreviewUrl = null;
        this.imagemRemovida = true;
        this.form.patchValue({ url_imagem_usuario: null });
        this.resetFileInput();
    }

    onUserImageError(): void {
        const currentImageUrl = this.user?.url_imagem_usuario?.trim?.() || null;

        if (!this.user?.id || this.isRefreshingUserImage || !currentImageUrl) {
            this.imageLoadFailed = true;
            return;
        }

        if (this.lastRetriedUserImageUrl === currentImageUrl) {
            this.imageLoadFailed = true;
            return;
        }

        this.lastRetriedUserImageUrl = currentImageUrl;
        this.isRefreshingUserImage = true;
        this.usuarioService.show(this.user.id).subscribe({
            next: (json) => {
                const renewedUrl = json?.url_imagem_usuario?.trim?.() || null;
                this.user = { ...this.user, ...json };
                localStorage.setItem('user', JSON.stringify(this.user));

                if (!renewedUrl || renewedUrl === currentImageUrl) {
                    this.imageLoadFailed = true;
                    return;
                }

                this.imageLoadFailed = false;
                this.lastRetriedUserImageUrl = null;
            },
            error: () => {
                this.imageLoadFailed = true;
            },
            complete: () => {
                this.isRefreshingUserImage = false;
            }
        });
    }

    onProfilePreviewImageError(event: Event): void {
        const img = event.target as HTMLImageElement | null;
        if (!img || img.src.includes(this.defaultUserImage)) {
            return;
        }

        const currentImageUrl = this.form?.get('url_imagem_usuario')?.value || this.user?.url_imagem_usuario || null;

        if (!this.user?.id || this.isRefreshingUserImage || !currentImageUrl) {
            img.src = this.defaultUserImage;
            return;
        }

        if (this.lastRetriedPreviewImageUrl === currentImageUrl) {
            img.src = this.defaultUserImage;
            return;
        }

        this.lastRetriedPreviewImageUrl = currentImageUrl;
        this.isRefreshingUserImage = true;
        this.usuarioService.show(this.user.id).subscribe({
            next: (json) => {
                this.user = { ...this.user, ...json };
                localStorage.setItem('user', JSON.stringify(this.user));
                const renewedUrl = json.url_imagem_usuario ?? null;
                this.form.patchValue({
                    url_imagem_usuario: renewedUrl,
                });

                if (!renewedUrl || renewedUrl === currentImageUrl) {
                    img.src = this.defaultUserImage;
                    return;
                }

                this.imagePreviewUrl = this.selectedImageFile ? this.imagePreviewUrl : renewedUrl;
                this.lastRetriedPreviewImageUrl = null;
                if (img && renewedUrl) {
                    img.src = renewedUrl;
                } else if (img) {
                    img.src = this.defaultUserImage;
                }
            },
            error: () => {
                img.src = this.defaultUserImage;
            },
            complete: () => {
                this.isRefreshingUserImage = false;
            }
        });
    }

    onProfileImageSelected(event: any): void {
        const file = event?.target?.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type?.startsWith('image/')) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Arquivo inválido',
                detail: 'Selecione um arquivo de imagem válido.',
            });
            this.resetFileInput();
            return;
        }

        this.selectedImageFile = file;
        this.imagemRemovida = false;
        const reader = new FileReader();
        reader.onload = () => {
            this.imagePreviewUrl = typeof reader.result === 'string' ? reader.result : null;
        };
        reader.readAsDataURL(file);
        this.resetFileInput();
    }

    getRandomColor(): string {
        const colors = [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
            '#9966FF', '#FF9F40', '#8AC24A', '#607D8B'
        ];
        const index = this.user.name ?
            this.user.name.charCodeAt(0) % colors.length : 0;
        return colors[index];
    }

    toggleMenu() {
        this.layoutService.onMenuProfileToggle();
    }

    get isHorizontal() {
        return this.layoutService.isHorizontal() && this.layoutService.isDesktop();
    }

    get menuProfileActive(): boolean {
        return this.layoutService.state.menuProfileActive;
    }

    get menuProfilePosition(): string {
        return this.layoutService.config.menuProfilePosition;
    }

    get isTooltipDisabled(): boolean {
        return !this.layoutService.isSlim();
    }

    logout() {
        localStorage.clear();
        this.router.navigate(['/login']);
    }

    changePassword() {
        this.resetProfileEditState();
        this.usuarioService.show(this.user.id).subscribe({
            next: (json) => {
                this.user = { ...this.user, ...json };
                localStorage.setItem('user', JSON.stringify(this.user));
                this.form.patchValue({
                    id: json.id,
                    name: json.name,
                    url_imagem_usuario: json.url_imagem_usuario ?? null,
                });
                this.imagePreviewUrl = json.url_imagem_usuario ?? null;
                this.displayModal = true;
            }
        });
    }

    save() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const newPassword = this.form.get('password')?.value;
        const currentPassword = this.form.get('current_password')?.value;
        const passwordConfirmation = this.form.get('password_confirmation')?.value;

        if (newPassword) {
            if (!currentPassword) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Senha atual obrigatória',
                    detail: 'Informe sua senha atual para alterar a senha.',
                });
                return;
            }

            if (newPassword !== passwordConfirmation) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Confirmação inválida',
                    detail: 'A confirmação da nova senha não confere.',
                });
                return;
            }
        }

        const formData = new FormData();
        formData.append('perfil_edicao', '1');
        formData.append('usuario[name]', this.form.get('name')?.value);

        if (newPassword) {
            formData.append('current_password', currentPassword);
            formData.append('usuario[password]', newPassword);
            formData.append('password_confirmation', passwordConfirmation);
        }

        if (this.selectedImageFile) {
            formData.append('imagem_usuario', this.selectedImageFile, this.selectedImageFile.name);
        } else if (this.imagemRemovida) {
            formData.append('remover_imagem', '1');
        }

        this.loading = true;
        this.usuarioService.updateMultipart(formData, this.form.get('id')?.value).subscribe({
            next: (json) => {
                this.user = { ...this.user, ...json };
                localStorage.setItem('user', JSON.stringify(this.user));
                this.imageLoadFailed = false;
                this.displayModal = false;
                this.resetProfileEditState();
            },
            error: (err) => {
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro ao salvar',
                    detail: err?.error?.message || 'Não foi possível atualizar seus dados.',
                });
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private refreshCurrentUser(): void {
        if (!this.user?.id) {
            return;
        }

        this.usuarioService.show(this.user.id).subscribe({
            next: (json) => {
                this.user = { ...this.user, ...json };
                localStorage.setItem('user', JSON.stringify(this.user));
                this.imageLoadFailed = false;
            },
            error: () => {
                this.imageLoadFailed = false;
            }
        });
    }

    private resetProfileEditState(): void {
        this.imageLoadFailed = false;
        this.selectedImageFile = null;
        this.imagePreviewUrl = null;
        this.imagemRemovida = false;
        this.lastRetriedUserImageUrl = null;
        this.lastRetriedPreviewImageUrl = null;
        this.resetFileInput();
        this.form.reset();
    }

    private resetFileInput(): void {
        if (this.profileImageUpload?.nativeElement) {
            this.profileImageUpload.nativeElement.value = '';
        }
    }
}
